import { type FC, type ReactNode, useState } from 'react'
import { Spinner } from '../../../components/Spinner/Spinner'
import { NavigationSidebar } from '../../../components/NavigationWithSideBar/NavigationSidebar'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetAccountSchedulingSettingsQuery, useUpdateAccountSchedulingSettingsMutation } from '../../../store/scheduling/scheduling'
import { type AccountSchedulingSettings } from '../../../models/Scheduling'
import { SharedEventTypesSection } from './sections/SharedEventTypesSection'
import { PublicBookingPageSection } from './sections/PublicBookingPageSection'
import { UserControlsSection } from './sections/UserControlsSection'
import { AvailabilitySection } from './sections/AvailabilitySection'

export type UpdateAccountSettings = (update: Partial<AccountSchedulingSettings>) => Promise<void>

const NAV_SECTIONS = [
  { id: 'event-types', label: 'Shared event types' },
  { id: 'booking-page', label: 'Public booking page' },
  { id: 'user-controls', label: 'User controls' },
  { id: 'availability', label: 'Availability' },
]

export const SchedulingSetupTab: FC = () => {
  const dispatch = useAppDispatch()
  const { hasScope } = useZocksAuth()
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const readOnly = !hasScope('**', 'account_config:update', ['account_config:admin'])
  const { data, isLoading } = useGetAccountSchedulingSettingsQuery({ accountId })
  const [updateAccountSettings, { isLoading: isUpdating }] = useUpdateAccountSchedulingSettingsMutation()
  const [activeSectionId, setActiveSectionId] = useState('event-types')
  const [pendingEdits, setPendingEdits] = useState<Partial<AccountSchedulingSettings>>({})

  if (isLoading || !data) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  const settings = { ...data, ...pendingEdits }

  const handleUpdate: UpdateAccountSettings = async (update) => {
    setPendingEdits((current) => ({ ...current, ...update }))
    const result = await updateAccountSettings({ accountId, update })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not save the setting', messages: ['Please try again.'] }))
    }
  }

  const sectionContent: Record<string, ReactNode> = {
    'event-types': <SharedEventTypesSection readOnly={readOnly} />,
    'booking-page': <PublicBookingPageSection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
    'user-controls': <UserControlsSection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
    availability: <AvailabilitySection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
  }

  return (
    <div className='d-flex flex-column gap-4'>
      {readOnly ? (
        <div className='paragraph-small zocks-text-gray-600 p-3 border rounded zocks-border-color-gray-200 zocks-bg-color-gray-100'>
          <i className='bi bi-info-circle me-1' />
          These Scheduling settings are managed by your account admins and are view-only for you.
        </div>
      ) : null}
      <div className='scroll-navigation__container'>
        <div className='d-flex flex-column gap-4'>
          <NavigationSidebar sections={NAV_SECTIONS} activeSectionId={activeSectionId} onSectionClick={setActiveSectionId} />
        </div>
        <div className='scroll-navigation__sections'>{sectionContent[activeSectionId]}</div>
      </div>
    </div>
  )
}
